import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'i18n'
import Cell from './PuzzleCell'
import HTML5Backend from 'react-dnd-html5-backend'
import { TouchBackend } from 'react-dnd-touch-backend'
import { DragDropContext } from 'react-dnd'
import * as styles from './PuzzleStep.module.scss'
import { isMobile, browserName } from 'react-device-detect'
import analytics from 'global/AnalyticsData'

/**
 * Shuffles the passed array and returns a new one
 *
 * @param  {Array} a
 * @return {Array}
 */
function shuffle(a) {
  const b = a.slice()

  for (let i = b.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[b[i], b[j]] = [b[j], b[i]]
  }

  return b
}

class Puzzle extends React.Component {
  constructor(props) {
    super(props)

    this.puzzleBoard = React.createRef()

    const { rows=3, cols=4 } = props
    const cells = rows * cols

    const isSponsored = Boolean(
      this.props.brk && this.props.brk.sponsoredType === 'sponsored',
    )

    const isTopNav = Boolean(this.props.brk?.navigationType === 'TOP')

    this.state = {
      isTopNav: isTopNav,
      isSponsored: isSponsored,
      positions: [...Array(cells).keys()],
      isSubmitted: false,
      isSolvedCorrectly: false,
      answerButtonText: false,
      seeSubmission: false,
      spacingMobile: 30, // 15px each side
      spacingDesktop: 80, // 40px each side
      breakpointMobile: 599, // this needs to be changed
      puzzleMaxWidth: isSponsored ? 670 : 800, // because why not?
      // Editorial breaks are wider hence need to be higher
      puzzleMaxHeight: isSponsored ? 502.5 : 600,
      movedFirstPiece: false,
    }

    this.handleSubmit = this.handleSubmit.bind(this)
    this.toggleAnswerButtonText = this.toggleAnswerButtonText.bind(this)
    this.toggleSeeSubmissionButton = this.toggleSeeSubmissionButton.bind(this)
  }

  updateDimensions = () => {
    this.setState({ width: window.innerWidth, height: window.innerHeight })
  }

  componentDidMount() {
    const { positions } = this.state
    this.updateDimensions()
    window.addEventListener('resize', this.updateDimensions)
    this.setState({ positions: shuffle(positions) })
  }

  componentDidUpdate() {
    const { cols=4, rows=3 } = this.props
    this.puzzleBoard.current.style.setProperty('--cols', cols)
    this.puzzleBoard.current.style.setProperty('--rows', rows)
  }

  onSwap(sourcePosition, dropPosition) {
    if (this.state.movedFirstPiece === false) {
      this.setState({ movedFirstPiece: true })
    }

    const oldPositions = this.state.positions.slice()
    const newPositions = []
    let done = true
    let p = 0

    for (let i in oldPositions) {
      i = Number(i)
      let value = oldPositions[i]
      let newValue = value

      if (value === sourcePosition) {
        newValue = dropPosition
      } else if (value === dropPosition) {
        newValue = sourcePosition
      }

      newPositions.push(newValue)

      if (newValue !== p) {
        done = false
      }

      p = p + 1
    }
    this.setState({ positions: newPositions })

    if (done) {
      this.setState({ isSolvedCorrectly: true })
    } else {
      this.setState({ isSolvedCorrectly: false })
    }
  }

  renderSquares() {
    const {
      step,
      image = `${process.env.SITE_ASSET_URL}/${step.puzzle.image.id}`,
      cols=4,
      rows=3,
    } = this.props
    const {
      positions,
      width,
      spacingMobile,
      spacingDesktop,
      breakpointMobile,
      puzzleMaxWidth,
      puzzleMaxHeight,
      isSubmitted,
    } = this.state

    const squares = positions.map(i => {
      return (
        <Cell
          key={i}
          width={
            width < breakpointMobile
              ? width - spacingMobile
              : width - spacingDesktop
          }
          height={
            width < breakpointMobile
              ? (width - spacingMobile) * 0.75
              : (width - spacingDesktop) * 0.75
          }
          image={image}
          rows={rows}
          cols={cols}
          puzzleMaxWidth={puzzleMaxWidth}
          puzzleMaxHeight={puzzleMaxHeight}
          position={i}
          onSwap={this.onSwap.bind(this)}
          isSubmitted={isSubmitted}
        />
      )
    })

    return squares
  }

  toggleAnswerButtonText() {
    analytics.stepEvent(
      `step_interaction_click_answer_button_${
        this.state.answerButtonText ? 'my_answers' : 'correct_answers'
      }`,
      this.props.step,
    )
    const { answerButtonText } = this.state
    this.setState({ answerButtonText: !answerButtonText })
  }

  toggleSeeSubmissionButton() {
    this.setState({ seeSubmission: true })
  }

  handleSubmit() {
    const { onEngage = function () {}, onAwardPoints = function () {} } =
      this.props
    this.setState({ isSubmitted: true })
    onEngage()
    onAwardPoints()
    analytics.stepEvent('step_interaction_puzzle_step', this.props.step)
  }

  render() {
    const { step, t } = this.props
    const {
      isSolvedCorrectly,
      isSubmitted,
      answerButtonText,
      seeSubmission,
      isSponsored,
      movedFirstPiece,
    } = this.state

    const content = (
      <div className={styles.puzzleStepContainer}>
        <div className={styles.puzzleContainer}>
          <div
            className={`${styles.imageResult} ${
              answerButtonText ? styles.show : styles.hide
            }`}
          >
            <img
              src={`${process.env.SITE_ASSET_URL}/${step.puzzle.image.id}`}
              alt=""
            />
          </div>
          <div
            ref={this.puzzleBoard}
            className={`${styles.puzzleStep} ${
              !answerButtonText ? styles.showGrid : styles.hide
            }`}
          >
            {this.renderSquares()}
          </div>
          {isSubmitted && !seeSubmission && (
            <div className={styles.resultModal}>
              {isSolvedCorrectly ? (
                <>
                  <h3>{t('puzzle:result.successTitle')}</h3>
                  <p>{t('puzzle:result.successBody')}</p>
                </>
              ) : (
                <h3>{t('puzzle:result.failureTitle')}</h3>
              )}
            </div>
          )}
        </div>
        {/* {Should always show the button for sponsored breaks} */}
        {/* {Only show the button when solved incorrectly for editoral breaks} */}
        {!(isSubmitted && isSolvedCorrectly && !isSponsored) && (
          <div className={this.state.isSponsored ? styles.buttonWrap : ''}>
            <div className={styles.submitContainer}>
              {!isSubmitted && (
                <button
                  className={styles.submit}
                  disabled={isSubmitted || movedFirstPiece === false}
                  onClick={this.handleSubmit}
                >
                  {t('common:submit')}
                </button>
              )}
              {isSubmitted && !isSolvedCorrectly && (
                <button
                  className={styles.submit}
                  onClick={this.toggleAnswerButtonText}
                >
                  {answerButtonText
                    ? t('puzzle:showMyAnswers')
                    : t('puzzle:showCorrectAnswers')}
                </button>
              )}
              {isSubmitted && isSolvedCorrectly && isSponsored && (
                <button
                  className={styles.submit}
                  onClick={this.toggleSeeSubmissionButton}
                >
                  {t('puzzle:seeMySubmission')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    )

    const header = (
      <div>
        <h1
          className={`step-content__title`}
          dangerouslySetInnerHTML={{ __html: step.puzzle.headline }}
        ></h1>
        <h4
          className={styles.introCopyWrap}
          dangerouslySetInnerHTML={{ __html: step.puzzle.introCopy }}
        ></h4>
      </div>
    )

    return this.state.isSponsored ? (
      <div
        className={`step-content with-shadow ${
          this.state.isTopNav ? 'top-navigation' : ''
        }`}
      >
        {header}
        {content}
        {this.props.skipButton}
        {this.props.nextStepButton}
      </div>
    ) : (
      content
    )
  }
}

Puzzle.propTypes = {
  image: PropTypes.string.isRequired,
  onDone: PropTypes.func,
}

const conditionalRender = () => {
  return isMobile ? TouchBackend : HTML5Backend
}

const PuzzleWithDnd = DragDropContext(conditionalRender())(Puzzle)

// Puzzle is a class component, so `t` is provided by this thin wrapper that
// calls the hook at the top level and passes it down as a prop.
const PuzzleStep = props => {
  const { t } = useTranslation(['puzzle', 'common'])

  return <PuzzleWithDnd {...props} t={t} />
}

export default PuzzleStep
